import { getBalancingServicesVolumeData } from "api/dataVisualisation/balancingServicesVolume/balancingServicesVolume";
import { getBidOfferData } from "api/dataVisualisation/bidOffer/bidOffer";
import { getPerBmuBidOfferAcceptancesData } from "api/dataVisualisation/bidOfferAcceptances/bidOfferAcceptances";
import {
  getDynamicData,
  getDynamicParametersData,
  getDynamicRatesData,
} from "api/dataVisualisation/dynamic/dynamic";
import { getPerBmuPhysicalData } from "api/dataVisualisation/physical/physical";
import { CheckboxDataGroup } from "components/components/CheckboxList/CheckboxList";
import {
  CheckboxGroupParent,
  flattenCheckboxDataGroups,
} from "components/dataVisualization/balancingMechanismTabs/DataExporter/shared/checkboxGroupHelpers";
import { DownloadCallback } from "components/dataVisualization/dataPageComponents/MultiDatasetExporter/MultiDatasetExporter";
import { isDynamicDataset } from "models/bmuData/dynamicDataModel";
import { isDynamicParametersDataset } from "models/bmuData/dynamicParametersDataModel";
import { isRatesDataset } from "models/bmuData/dynamicRatesDataModel";
import { isPhysicalDataset } from "models/bmuData/physicalDataModel";
import { DataDownloadFormat } from "models/dataDownload/dataDownloadFormat";
import DateFilterModel from "models/filterModels/dateFilterModel";
import featureFlags from "utils/featureFlags";

export const transformPerBmuDownloadCallbacks = (
  selectedDatasets: string[],
  dateFilter: DateFilterModel,
  bmuId: string
): DownloadCallback[] => {
  const downloadCallbacks: DownloadCallback[] = [];

  const physicalDatasets = selectedDatasets.filter(isPhysicalDataset);

  if (physicalDatasets.length > 0) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getPerBmuPhysicalData(dateFilter, bmuId, physicalDatasets, format),
      filePrefix: "PhysicalData",
    });
  }

  const dynamicDatasets = selectedDatasets.filter(isDynamicDataset);
  if (dynamicDatasets.length > 0) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getDynamicData(dateFilter, bmuId, dynamicDatasets, format),
      filePrefix: "DynamicData",
    });
  }

  const ratesDatasets = selectedDatasets.filter(isRatesDataset);
  if (ratesDatasets.length > 0) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getDynamicRatesData(dateFilter, bmuId, ratesDatasets, format),
      filePrefix: "DynamicRatesData",
    });
  }

  const dynamicParametersDatasets = selectedDatasets.filter(
    isDynamicParametersDataset
  );
  if (dynamicParametersDatasets.length > 0) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getDynamicParametersData(
          dateFilter,
          bmuId,
          dynamicParametersDatasets,
          format
        ),
      filePrefix: "DynamicParametersData",
    });
  }

  if (selectedDatasets.includes("BOD")) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getBidOfferData(dateFilter, bmuId, format),
      filePrefix: "BidOfferData",
    });
  }

  if (selectedDatasets.includes("BOAL")) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getPerBmuBidOfferAcceptancesData(dateFilter, bmuId, format),
      filePrefix: "BidOfferAcceptancesData",
    });
  }

  if (selectedDatasets.includes("QAS")) {
    downloadCallbacks.push({
      download: (format: DataDownloadFormat) =>
        getBalancingServicesVolumeData(dateFilter, [bmuId], format),
      filePrefix: "BalancingServicesVolumeData",
    });
  }

  return downloadCallbacks;
};

const bmuCheckboxDataGroups: CheckboxGroupParent[] = [
  {
    name: "Physical data",
    children: [
      { name: "Bid offer acceptance level (BOAL)", datasets: ["BOAL"] },
      { name: "Final physical notification (FPN)", datasets: ["PN"] },
      { name: "Maximum export limit (MEL)", datasets: ["MELS"] },
      { name: "Maximum import limit (MIL)", datasets: ["MILS"] },
      { name: "Quiescent physical notification (QPN)", datasets: ["QPN"] },
      { name: "Stable export limit (SEL)", datasets: ["SEL"] },
      { name: "Stable import limit (SIL)", datasets: ["SIL"] },
    ],
  },
  {
    name: "Dynamic data",
    children: [
      {
        // TODO 316294: Remove feature flag checks when dynamic parameters are fully implemented
        name: featureFlags.displayDynamicParameters
          ? "Maximum delivery (Superseded)"
          : "Maximum delivery",
        datasets: ["MDV", "MDP"],
      },
      ...(featureFlags.displayDynamicParameters
        ? [{ name: "Maximum delivery offer/bid", datasets: ["MDB", "MDO"] }]
        : []),
      { name: "Minimum periods", datasets: ["MZT", "MNZT"] },
      { name: "Notices", datasets: ["NDZ", "NTB", "NTO"] },
      { name: "Rates", datasets: ["RDRE", "RURE", "RDRI", "RURI"] },
      { name: "Stable limits", datasets: ["SIL", "SEL"] },
    ],
  },
  {
    name: "Bid offer data",
    children: [
      { name: "Bid offer pairs", datasets: ["BOD"] },
      { name: "Final physical notification (FPN)", datasets: ["PN"] },
    ],
  },
  {
    name: "Balancing services volume data",
    datasets: ["QAS"],
  },
];

export const getBmuCheckboxDataGroups = (): CheckboxDataGroup[] =>
  flattenCheckboxDataGroups(bmuCheckboxDataGroups);
